import { createContext, use } from 'react';
import type { LoginInput, Session } from '@/api/session';
import type { Ability } from './abilities';
import type { Environment } from './token-store';

export type SessionState =
  | { status: 'loading' }
  | { status: 'anonymous'; reason?: 'expired' | 'signed_out' }
  | { status: 'error'; error: unknown }
  | { status: 'authenticated'; session: Session };

export interface SessionContextValue {
  state: SessionState;
  login: (input: LoginInput) => Promise<Session>;
  logout: () => Promise<void>;
  /** Move to another tenant and/or environment. Issues a new token; all cached data is dropped. */
  switchContext: (target: { tenantId?: string; environment: Environment }) => Promise<Session>;
  switching: boolean;
  retry: () => void;
}

export const SessionContext = createContext<SessionContextValue | null>(null);

export function useSessionContext(): SessionContextValue {
  const ctx = use(SessionContext);
  if (!ctx) throw new Error('useSessionContext must be used inside <SessionProvider>');
  return ctx;
}

/** The signed-in session. Only call below <RequireAuth>. */
export function useSession() {
  const ctx = useSessionContext();
  if (ctx.state.status !== 'authenticated') throw new Error('useSession requires an authenticated session');
  const session = ctx.state.session;
  const abilities = session.abilities;
  return {
    session,
    user: session.user,
    tenant: session.tenant,
    environment: session.environment,
    isTest: session.environment === 'test',
    can: (ability: Ability) => abilities.includes(ability),
    canAny: (...list: Ability[]) => list.some((a) => abilities.includes(a)),
    logout: ctx.logout,
    switchContext: ctx.switchContext,
    switching: ctx.switching,
  };
}
